import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { apiErrorMessage } from '../../services/errorMessage';
import StoryCard from './StoryCard';
import CreateFromProposal from './CreateFromProposal';
import TagFilter from './TagFilter';
import ManageTagsDrawer from './ManageTagsDrawer';
import { LoadingRegion } from '../common/icons';
import { sharedSkipReason, sortStories, stableOrder } from './proposalGrouping';
import type { TrendProposal, TrendStory, TrendTag, WebSocketEvent } from '../../types';

/**
 * Section A of the Trends page: what is spiking, and what is worth writing.
 *
 * This is the page's primary content now, expanded by default. It used to be a
 * collapsed panel that fetched nothing until clicked, which put the only
 * genuinely new information on the screen behind a disclosure triangle and
 * offered "add this term to a topic" as its one verb.
 *
 * Enrichment is kicked off by the READ — see the endpoint's docstring. A page
 * whose whole argument is "here is what is worth writing" cannot open empty and
 * wait to be asked.
 */

/** How far back to show terms that have dropped out of Google's feed.
 *
 * Five days by default. A spike runs a day or two, so this covers the window in
 * which a term you researched is still worth acting on, without turning the
 * page into an archive you have to scroll past to reach today. */
const WINDOWS = [
  { label: 'Trending now', days: 1 },
  { label: 'Last 5 days', days: 5 },
  { label: 'Last 2 weeks', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const GEOS = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'IE', label: 'Ireland' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'IN', label: 'India' },
];

function freshness(ageSeconds: number | null): string {
  if (ageSeconds == null) return '';
  if (ageSeconds < 120) return 'just now';
  if (ageSeconds < 3600) return `${Math.round(ageSeconds / 60)}m ago`;
  return `${Math.round(ageSeconds / 3600)}h ago`;
}

export default function StoriesWorthAPage() {
  const [geo, setGeo] = useState('US');
  const [days, setDays] = useState(5);
  const [tags, setTags] = useState<string[]>([]);
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const [tagsAvailable, setTagsAvailable] = useState<TrendTag[]>([]);
  const [tagsWanted, setTagsWanted] = useState<TrendTag[]>([]);
  const [managingTags, setManagingTags] = useState(false);
  const [stories, setStories] = useState<TrendStory[]>([]);
  const [ageSeconds, setAgeSeconds] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Terms we know a worker is on. Held locally because the enqueue response
  // lands before the first websocket event, and a card that looks idle in that
  // gap invites a second click and a second Google Ads call.
  const [working, setWorking] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState<TrendProposal | null>(null);
  /* Terms whose research finished while the reader was on this screen. They get
     a highlight so the eye can find the one it was following — a card that
     changes shape silently, in a list of twenty near-identical cards, is a card
     you have to re-find by reading. */
  const [justDone, setJustDone] = useState<Set<number>>(new Set());
  /* The order decided by the last deliberate action. Background refetches reuse
     it (see stableOrder) so a landing result never moves the card under you. */
  const orderRef = useRef<number[]>([]);
  const { subscribe } = useWebSocket();
  const geoRef = useRef(geo);
  geoRef.current = geo;
  const daysRef = useRef(days);
  daysRef.current = days;
  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  const load = useCallback(
    async (opts: { refresh?: boolean } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getTrendStories({ geo, days, tags, refresh: opts.refresh });
        // A load is a deliberate act — first visit, Refresh, a changed geo or
        // window — so this is where the order is allowed to change.
        const ordered = sortStories(res.items);
        orderRef.current = ordered.map(s => s.trending_search_id);
        setStories(ordered);
        setTagCounts(res.tag_counts ?? {});
        setTagsAvailable(res.tags_available ?? []);
        setTagsWanted(res.tags_wanted ?? []);
        setAgeSeconds(res.age_seconds);
        // A populated list WITH an error means the feed was unreachable and the
        // cache carried us — worth saying, not worth blanking the panel over.
        setError(res.error);
        setWorking(new Set(res.enriching_ids));
      } catch (e) {
        setError(apiErrorMessage(e));
        setStories([]);
      } finally {
        setLoading(false);
      }
    },
    [geo, days, tags],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Refetch when a brief lands. The whole card changes shape — demand numbers,
  // proposals, skip reasons — so a targeted patch would be more code for the
  // same result, and the read is cache-fronted.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'trend_brief') return;
      const id = event.trending_search_id;
      if (event.status === 'started') {
        setWorking(prev => new Set(prev).add(id));
        return;
      }
      setWorking(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (event.status === 'ready' || event.status === 'failed') {
        // Mark it, and leave the mark up. It clears when the reader acts —
        // creating, dismissing or reloading — rather than on a timer, because a
        // highlight that expires while you are reading a different card is a
        // highlight that was not there when you looked up.
        setJustDone(prev => new Set(prev).add(id));
        // Don't re-enrich on the refetch — the work just finished.
        api
          .getTrendStories({
            geo: geoRef.current,
            days: daysRef.current,
            tags: tagsRef.current,
            autoEnrich: false,
          })
          .then(res => {
            // NOT sortStories: this refetch is a consequence of a result
            // landing, and re-ranking here is what made the card jump.
            setStories(stableOrder(res.items, orderRef.current));
            setTagCounts(res.tag_counts ?? {});
            setTagsAvailable(res.tags_available ?? []);
            setTagsWanted(res.tags_wanted ?? []);
            setAgeSeconds(res.age_seconds);
          })
          .catch(() => {});
      }
    });
  }, [subscribe]);

  const handleResearch = async (story: TrendStory) => {
    setWorking(prev => new Set(prev).add(story.trending_search_id));
    try {
      await api.requestTrendBrief(story.term, geo, story.brief?.status === 'READY');
    } catch (e) {
      setError(apiErrorMessage(e));
      setWorking(prev => {
        const next = new Set(prev);
        next.delete(story.trending_search_id);
        return next;
      });
    }
  };

  const handleDismiss = async (proposal: TrendProposal) => {
    // Optimistic: the row stays in the DB so a re-research cannot resurrect it,
    // but the card should not wait on a round trip to stop showing it.
    setStories(prev =>
      prev.map(s =>
        s.brief
          ? { ...s, brief: { ...s.brief, proposals: s.brief.proposals.filter(p => p.id !== proposal.id) } }
          : s,
      ),
    );
    try {
      await api.dismissTrendProposal(proposal.id);
    } catch {
      load();
    }
  };

  // One account-level setting, said once, instead of once per card.
  const hoisted = sharedSkipReason(stories);

  return (
    <section className="mb-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Stories worth a page</h2>
        {/* What a Create actually produces. The cards carry a lot of prose and
            a blue button; without this the only way to learn that the output is
            a page — the same kind of artifact the rest of the app builds and
            deploys — is to press it. */}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Spiking searches, researched into pages you could build.{' '}
          <span className="text-gray-400 dark:text-gray-500">
            #1 is what Google ranks highest right now; · means it has dropped out
            of the feed.
          </span>
        </span>
        <select
          value={geo}
          onChange={e => setGeo(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
        >
          {GEOS.map(g => (
            <option key={g.code} value={g.code}>{g.label}</option>
          ))}
        </select>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          title="Terms that have dropped out of Google's feed stay visible for this long"
        >
          {WINDOWS.map(w => (
            <option key={w.days} value={w.days}>{w.label}</option>
          ))}
        </select>
        <button
          onClick={() => load({ refresh: true })}
          disabled={loading}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {ageSeconds != null && !loading && (
          <span className="text-xs text-gray-400 dark:text-gray-500">feed updated {freshness(ageSeconds)}</span>
        )}
        {/* Results land while you are reading something else, and the card that
            changed may be off screen. This says something happened and gives it
            a number; the cards themselves carry the ring that says which. */}
        {justDone.size > 0 && (
          <button
            onClick={() => {
              const first = stories.find(s => justDone.has(s.trending_search_id));
              if (!first) return;
              document
                .getElementById(`trend-card-${first.trending_search_id}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60"
          >
            {justDone.size} just researched - show me
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          {error}
        </div>
      )}

      {hoisted && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          <span>{hoisted}</span>
          <a href="/settings?tab=connections" className="font-medium underline">
            Open connection settings
          </a>
        </div>
      )}

      <TagFilter
        counts={tagCounts}
        available={tagsAvailable}
        wanted={tagsWanted}
        selected={tags}
        onChange={setTags}
        onPromote={async tag => {
          // Optimistic: the chip should move the moment it is clicked. A reload
          // follows because promoting changes what the model may return next.
          setTagsWanted(prev => prev.filter(t => t.id !== tag.id));
          try {
            await api.promoteTrendTag(tag.id);
            load();
          } catch {
            load();
          }
        }}
        onDismiss={async tag => {
          setTagsWanted(prev => prev.filter(t => t.id !== tag.id));
          try {
            await api.dismissTrendTag(tag.id);
          } catch {
            load();
          }
        }}
        onManage={() => setManagingTags(true)}
        onAdd={async value => {
          try {
            await api.addTrendTag({ value });
            load();
          } catch (e) {
            setError(apiErrorMessage(e));
          }
        }}
      />

      {!loading && !stories.length && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {tags.length
            ? 'No trending stories match those tags in this window.'
            : 'No trending searches for this country.'}
        </p>
      )}

      {/* THE FIRST LOAD, said out loud.
          The empty-state copy above is correctly suppressed while loading — it would be
          claiming there is nothing to show before anyone has looked — but that left the
          arrival on this page as a heading, two selects and blank space, which reads as
          "no trending searches" just as convincingly.

          Only when there is nothing on screen yet. Changing the window or a tag chip
          re-runs `load` with the old stories still rendered; swapping them for a spinner
          would throw away the list you are filtering to make one appear. */}
      {loading && !stories.length && <LoadingRegion label="Finding what's spiking…" />}

      <div className="space-y-2">
        {stories.map(story => (
          <StoryCard
            key={story.trending_search_id}
            story={story}
            enriching={working.has(story.trending_search_id)}
            knownTags={tagsAvailable}
            justResearched={justDone.has(story.trending_search_id)}
            onSeen={() =>
              setJustDone(prev => {
                const next = new Set(prev);
                next.delete(story.trending_search_id);
                return next;
              })
            }
            hoistedReason={hoisted}
            onResearch={handleResearch}
            onCreate={setCreating}
            onDismiss={handleDismiss}
          />
        ))}
      </div>

      {managingTags && (
        <ManageTagsDrawer
          onClose={() => setManagingTags(false)}
          // The chips behind the drawer change shape as tags move between
          // groups, so the list re-reads rather than waiting for a poll.
          onChanged={load}
        />
      )}

      {creating && (
        <CreateFromProposal
          proposal={creating}
          onClose={() => setCreating(null)}
          onCreated={() => setCreating(null)}
        />
      )}
    </section>
  );
}
